import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";

import { api, type Feeder, type Substation, type GridOptions } from "@/lib/api";
import { CatalogChoice } from "@/components/catalog-choice";
import { errorStep, FormStepNavigation } from "@/components/form-step-navigation";
import { useAuth } from "@/auth/auth-provider";
import { hasCapability } from "@/auth/capabilities";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/page-header";
import { ListFilter } from "@/components/list-filter";
import { AdminListLayout, AdminFilterPanel, AdminFilterGroup, AdminFilterDisclosure } from "@/components/admin-filter-panel";
import { ListPagination, ListSearch, ListSort, useBrowsingState, usePagedList } from "@/components/list-controls";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchableSelector } from "@/components/searchable-selector";
import { substationPickerOption } from "@/components/substation-picker-option";
import { useDialogDraft } from "@/components/dialog-draft";

const feederSchema = z.object({
  display_name: z.string().trim().min(1).max(128),
  voltage_kv: z.number().positive().nullable(),
  upstream_feeder_name: z.string().trim().max(128).nullable(),
  upstream_voltage_kv: z.number().positive().nullable(),
  source_voltage_kv: z.number().positive().nullable(),
  feeder_location: z.string().nullable(),
  major_streets: z.string().nullable(),
  served_states: z.array(z.string()),
  regulators: z.array(z.string()),
  nerc_service_classes: z.array(z.string()),
  metered_customers: z.number().int().nonnegative().nullable(),
  unmetered_customers: z.number().int().nonnegative().nullable(),
  iot_vendor: z.string().nullable(),
  iot_phase: z.string().nullable(),
  iot_installed: z.boolean().nullable(),
  feeder_type: z.string().nullable(),
  feeder_type_detail: z.string().nullable(),
  external_key: z.string().trim().min(1).max(128),
  feeder_name: z.string().max(128),
  substation_id: z.coerce.number().int().positive(),
  latitude: z.coerce.number().nullable(),
  longitude: z.coerce.number().nullable(),
  power_transformer_id: z.string().max(128),
  power_transformer_capacity_mva: z.coerce.number().nullable(),
  feeder_band: z.string().max(32),
  feeder_voltage: z.string().max(32),
  ct_vt_condition: z.string().max(128),
  ct_primary: z.coerce.number().int().positive(),
  ct_secondary: z.coerce.number().int().positive(),
  vt_primary: z.coerce.number().int().positive(),
  vt_secondary: z.coerce.number().int().positive(),
  station_dc_nominal_v: z.coerce.number().int().nullable(),
  station_dc_min_v: z.coerce.number().int().nullable(),
  station_dc_max_v: z.coerce.number().int().nullable(),
  preferred_network: z.string().max(64),
  installation_type_available: z.string().max(128),
  remarks: z.string().max(512),
});
type FeederValues = z.infer<typeof feederSchema>;

function feederValues(feeder: Feeder): FeederValues {
  return { ...feeder, display_name: feeder.display_name ?? feeder.feeder_name,
    voltage_kv: feeder.voltage_kv ?? null, feeder_location: feeder.feeder_location ?? "",
    upstream_feeder_name: feeder.upstream_feeder_name ?? "",
    upstream_voltage_kv: feeder.upstream_voltage_kv ?? null, source_voltage_kv: feeder.source_voltage_kv ?? null,
    major_streets: feeder.major_streets ?? "", served_states: feeder.served_states ?? [],
    regulators: feeder.regulators ?? [], nerc_service_classes: feeder.nerc_service_classes ?? [],
    metered_customers: feeder.metered_customers ?? null, unmetered_customers: feeder.unmetered_customers ?? null,
    iot_vendor: feeder.iot_vendor ?? "", iot_phase: feeder.iot_phase ?? "", iot_installed: feeder.iot_installed ?? null,
    feeder_type: feeder.feeder_type ?? "", feeder_type_detail: feeder.feeder_type_detail ?? "", power_transformer_capacity_mva: feeder.power_transformer_capacity_mva };
}

function feederPayload(body: FeederValues): Parameters<typeof api.updateFeeder>[1] {
  return {
    ...body,
    feeder_name: body.display_name,
    upstream_feeder_name: body.upstream_feeder_name?.trim() || null,
    feeder_location: body.feeder_location?.trim() || null,
    major_streets: body.major_streets?.trim() || null,
    iot_vendor: body.iot_vendor?.trim() || null,
    iot_phase: body.iot_phase?.trim() || null,
    feeder_type: body.feeder_type?.trim() || null,
    feeder_type_detail: body.feeder_type_detail?.trim() || null,
    latitude: body.latitude === null || Number.isNaN(body.latitude) ? null : body.latitude,
    longitude: body.longitude === null || Number.isNaN(body.longitude) ? null : body.longitude,
    power_transformer_capacity_mva: body.power_transformer_capacity_mva === null ||
      Number.isNaN(body.power_transformer_capacity_mva) ? null : body.power_transformer_capacity_mva,
  };
}

export function FeedersPage(): JSX.Element {
  const { me } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const canEdit = hasCapability(me, "catalog:write");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const feedersQuery = useQuery({ queryKey: ["feeders"], queryFn: () => api.feeders() });
  const optionsQuery = useQuery({ queryKey: ["grid-options"], queryFn: () => api.gridOptions() });
  const substationsQuery = useQuery({
    queryKey: ["substations"],
    queryFn: () => api.substations(),
  });
  const [editing, setEditing] = useState<Feeder | null>(null);
  const [deleting, setDeleting] = useState<Feeder | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const emptyFilters = { company: "", station: "", voltage: "", band: "", assignment: "", verification: "",
    vendor: "", regulator: "", state: "", serviceClass: "", phase: "", installed: "" };
  const [filters, setFilters] = useBrowsingState("feeders:filters", emptyFilters);
  const [createCompanyId, setCreateCompanyId] = useState<number | undefined>();
  const draft = useDialogDraft();
  const resetDraft = draft.reset;
  useEffect(() => {
    if (!createOpen && editing === null) resetDraft();
  }, [createOpen, editing, resetDraft]);

  const form = useForm<FeederValues>({
    resolver: zodResolver(feederSchema),
    shouldUnregister: false,
    defaultValues: {
      display_name: "",
      voltage_kv: null,
      upstream_feeder_name: "",
      upstream_voltage_kv: null,
      source_voltage_kv: null,
      feeder_location: "",
      major_streets: "",
      served_states: [],
      regulators: [],
      nerc_service_classes: [],
      metered_customers: null,
      unmetered_customers: null,
      iot_vendor: "",
      iot_phase: "",
      iot_installed: null,
      feeder_type: "",
      feeder_type_detail: "",
      external_key: "",
      feeder_name: "",
      substation_id: 0,
      latitude: null,
      longitude: null,
      power_transformer_id: "",
      power_transformer_capacity_mva: null,
      feeder_band: "",
      feeder_voltage: "",
      ct_vt_condition: "",
      ct_primary: 1,
      ct_secondary: 1,
      vt_primary: 1,
      vt_secondary: 1,
      station_dc_nominal_v: null,
      station_dc_min_v: null,
      station_dc_max_v: null,
      preferred_network: "",
      installation_type_available: "",
      remarks: "",
    },
  });

  const createDefaults = useRef(form.getValues());
  const mutation = useMutation({
    mutationFn: async (input: { feederId?: number; body: FeederValues }) => {
      const payload = feederPayload(input.body);
      if (input.feederId === undefined) {
        return api.createFeeder(payload);
      }
      return api.updateFeeder(input.feederId, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["feeders"] });
      setCreateOpen(false);
      setEditing(null);
      toast({ title: "Feeder saved", variant: "success" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Feeder save failed",
        description: error instanceof Error ? error.message : "Unknown",
        variant: "destructive",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (feederId: number) => api.deleteFeeder(feederId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["feeders"] });
      setDeleting(null);
      toast({ title: "Feeder deleted", variant: "success" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Feeder delete failed",
        description: error instanceof Error ? error.message : "Unknown",
        variant: "destructive",
      }),
  });
  const { list, query: feederPage } = usePagedList<Feeder>("feeders", {
    distribution_company_id: filters.company,
    substation_id: filters.station,
    feeder_voltage: filters.voltage,
    feeder_band: filters.band,
    assignment: filters.assignment,
    verification: filters.verification,
    vendor: filters.vendor,
    regulator: filters.regulator,
    state: filters.state,
    service_class: filters.serviceClass,
    phase: filters.phase,
    installed: filters.installed,
  }, [
    { key: "name", label: "Name" },
    { key: "external_key", label: "External key" },
    { key: "company", label: "Distribution company" },
    { key: "substation", label: "Substation" },
    { key: "voltage", label: "Voltage" },
  ], api.browseFeeders);
  const substations: Substation[] = useMemo(
    () => substationsQuery.data ?? [],
    [substationsQuery.data],
  );

  function openCreate(): void {
    mutation.reset();
    form.reset(createDefaults.current);
    draft.reset();
    setCreateCompanyId(undefined);
    setCreateOpen(true);
  }

  useEffect(() => {
    const state = location.state as { substationId?: number; createFeeder?: boolean } | null;
    if (!state?.substationId || !substationsQuery.data) return;
    const station = substationsQuery.data.find((item) => item.substation_id === state.substationId);
    if (station) {
      setFilters({ ...emptyFilters, company: String(station.distribution_company_id), station: String(station.substation_id) });
      list.setPage(1);
      if (state.createFeeder && canEdit) {
        mutation.reset();
        form.reset({ ...createDefaults.current, substation_id: station.substation_id });
        draft.reset();
        setCreateCompanyId(station.distribution_company_id);
        setCreateOpen(true);
      }
    }
    navigate(location.pathname, { replace: true, state: null });
  // Consume the navigation state once after the station data loads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, substationsQuery.data, canEdit]);

  function openEdit(feeder: Feeder): void {
    mutation.reset();
    draft.reset();
    setEditing(feeder);
    form.reset(feederValues(feeder));
  }

  return (
    <>
      <PageHeader
        title="Feeders"
        description="Searchable feeder templates and active meter assignments."
        actions={
          <>
            {canEdit ? <Button onClick={openCreate}><Plus className="h-4 w-4" /> New feeder</Button> : null}
            {canEdit ? <Button asChild variant="outline"><Link to="/imports?type=feeders">Import data</Link></Button> : null}
          </>
        }
      />

      {optionsQuery.error ? <p role="alert" className="mb-4 text-sm text-destructive">Could not load shared grid choices. Saved values remain available; reload before selecting new metadata.</p> : null}
      <AdminListLayout filters={
        <AdminFilterPanel count={Object.values(filters).filter(Boolean).length + Number(Boolean(list.search.trim()))}
          onClear={() => { setFilters(emptyFilters); list.setSearch(""); }}>
        <AdminFilterGroup label="Search">
          <ListSearch
            label="Search feeders"
            placeholder="Name, location, streets, meters…"
            value={list.search}
            onChange={list.setSearch}
          />
          <ListSort {...list} />
        </AdminFilterGroup>
        <AdminFilterGroup label="Ownership and station">
          <ListFilter label="Distribution company filter" value={filters.company}
            options={Array.from(new Map((feedersQuery.data ?? []).map((feeder) => [String(feeder.distribution_company_id), feeder.distribution_company_name])).entries()).map(([value, label]) => ({ value, label }))}
            onChange={(company) => { setFilters({ ...filters, company, station: "" }); list.setPage(1); }} />
          <ListFilter label="Substation filter" value={filters.station}
            options={substations.filter((station) => !filters.company || String(station.distribution_company_id) === filters.company).map((station) => ({ value: String(station.substation_id), label: `${station.distribution_company_name} · ${station.name} · Type: ${station.station_type ?? "Unknown"} · ${station.external_key}` }))}
            onChange={(station) => { setFilters({ ...filters, station }); list.setPage(1); }} />
        </AdminFilterGroup>
        <AdminFilterGroup label="Assignment">
          <ListFilter label="Assignment filter" value={filters.assignment}
            options={[{ value: "assigned", label: "Assigned" }, { value: "unassigned", label: "Unassigned" }]}
            onChange={(assignment) => { setFilters({ ...filters, assignment }); list.setPage(1); }} />
        </AdminFilterGroup>
        <AdminFilterDisclosure label="More filters"
          count={[filters.voltage, filters.band, filters.vendor, filters.regulator, filters.state, filters.serviceClass, filters.phase, filters.installed, filters.verification].filter(Boolean).length}>
          <AdminFilterGroup label="Electrical classification">
            {(["voltage", "band"] as const).map((key) => (
              <ListFilter key={key} label={key === "voltage" ? "Voltage filter" : "Band filter"} value={filters[key]}
                options={Array.from(new Set((feedersQuery.data ?? []).map((feeder) => feeder[key === "voltage" ? "feeder_voltage" : "feeder_band"]).filter(Boolean))).sort().map((value) => ({ value, label: value }))}
                onChange={(value) => { setFilters({ ...filters, [key]: value }); list.setPage(1); }} />
            ))}
          </AdminFilterGroup>
          <AdminFilterGroup label="Operational status">
            {([
              ["vendor", "IoT Vendor filter", "iot_vendors"],
              ["regulator", "Regulator filter", "regulators"],
              ["state", "Feeder state (served) filter", "states"],
              ["serviceClass", "Type (NERC) filter", "nerc_service_classes"],
              ["phase", "IoT installation phase filter", "iot_phases"],
            ] as const).map(([key, label, optionKey]) => <ListFilter key={key} label={label} value={filters[key]}
              options={(optionsQuery.data?.[optionKey] ?? []).filter(Boolean).map((value) => ({ value, label: value }))}
              onChange={(value) => { setFilters({ ...filters, [key]: value }); list.setPage(1); }} />)}
            <ListFilter label="IoT installed filter" value={filters.installed}
              options={[{ value: "unknown", label: "Unknown" }, { value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
              onChange={(installed) => { setFilters({ ...filters, installed }); list.setPage(1); }} />
            <ListFilter label="CT/VT verification filter" value={filters.verification}
              options={[{ value: "verified", label: "Both verified" }, { value: "unverified", label: "Unverified CT or VT" }]}
              onChange={(verification) => { setFilters({ ...filters, verification }); list.setPage(1); }} />
          </AdminFilterGroup>
        </AdminFilterDisclosure>
        </AdminFilterPanel>
      }>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          {feederPage.isLoading ? (
            <div role="status" aria-label="Loading feeders" className="p-6">
              <Skeleton className="h-80 w-full" />
            </div>
          ) : feederPage.error ? (
            <div role="alert" className="p-6">
              Could not load feeders. <Button variant="outline" onClick={() => void feederPage.refetch()}>Retry</Button>
            </div>
          ) : (
            <table className="w-full min-w-[900px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Feeder</th>
                  <th className="px-4 py-3">Substation</th>
                  <th className="px-4 py-3">Voltage</th>
                  <th className="px-4 py-3">Grid profile</th>
                  <th className="px-4 py-3">Connected meters</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.total === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-muted-foreground" colSpan={6}>
                      {[filters.company, filters.station, filters.voltage, filters.band, filters.assignment,
                        filters.verification, filters.vendor, filters.regulator, filters.state, filters.serviceClass,
                        filters.phase, filters.installed, list.search.trim()].some(Boolean)
                        ? "No feeders match the current filter."
                        : "No feeders found."}
                    </td>
                  </tr>
                ) : (
                  list.rows.map((feeder) => (
                    <tr key={feeder.feeder_id} className="border-b border-border last:border-0 hover:bg-accent/40">
                      <td className="px-4 py-3">
                        <Link to={`/feeders/${feeder.feeder_id}`} className="font-semibold text-foreground hover:text-primary">{feeder.feeder_name}</Link>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {feeder.external_key}
                        </div>
                      </td>
                      <td className="px-4 py-3">{feeder.substation_name}</td>
                      <td className="px-4 py-3">{feeder.feeder_voltage}</td>
                      <td className="max-w-52 px-4 py-3 text-xs leading-5">
                        <div>Regulator: {feeder.regulators?.filter((value) => value.trim()).join(", ") || "Unknown"}</div>
                        <div>Served: {feeder.served_states?.filter((value) => value.trim()).join(", ") || "Unknown"}</div>
                        <div>NERC: {feeder.nerc_service_classes?.filter((value) => value.trim()).join(", ") || "Unknown"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {feeder.assignments.length === 0 ? (
                          <span className="text-xs text-muted-foreground">Unassigned</span>
                        ) : (
                          <div className="max-w-56 space-y-1">
                            {feeder.assignments.slice(0, 1).map((assignment) => (
                              <Link
                                key={assignment.controller_meter_id}
                                to={`/controllers/${encodeURIComponent(assignment.device_id)}/meters/${assignment.modbus_id}`}
                                className="block truncate font-medium text-foreground hover:text-primary"
                              >
                                {assignment.meter_label}
                              </Link>
                            ))}
                            {feeder.assignments.length > 1 ? (
                              <div className="text-muted-foreground">+{feeder.assignments.length - 1} more</div>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/feeders/${feeder.feeder_id}`}>Details</Link>
                          </Button>
                          {canEdit ? (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openEdit(feeder)}>
                                <Pencil className="h-4 w-4" /> Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => setDeleting(feeder)}
                              >
                                <Trash2 className="h-4 w-4" /> Delete
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">Read only</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </CardContent>
        <ListPagination {...list} />
      </Card>
      </AdminListLayout>

      <Dialog open={createOpen} onOpenChange={(open) => { if (open) setCreateOpen(true); else if (draft.canClose(mutation.isPending)) setCreateOpen(false); }}>
        <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-3xl"
          onChangeCapture={draft.onChangeCapture} onClickCapture={draft.onClickCapture} onKeyDownCapture={draft.onKeyDownCapture}>
          <FeederForm
            title="Create feeder"
            onSubmit={(values) => mutation.mutateAsync({ body: values })}
            onCancel={() => { if (draft.canClose(mutation.isPending)) setCreateOpen(false); }}
            submitting={mutation.isPending}
            saveError={mutation.error}
            form={form}
            substations={substations}
            initialCompanyId={createCompanyId}
            options={optionsQuery.data}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => { if (!open && draft.canClose(mutation.isPending)) setEditing(null); }}>
        <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-3xl"
          onChangeCapture={draft.onChangeCapture} onClickCapture={draft.onClickCapture} onKeyDownCapture={draft.onKeyDownCapture}>
          {editing ? (
            <FeederForm
              title={`Edit ${editing.feeder_name}`}
              onSubmit={(values) => mutation.mutateAsync({ feederId: editing.feeder_id, body: values })}
              onCancel={() => { if (draft.canClose(mutation.isPending)) setEditing(null); }}
              submitting={mutation.isPending}
              saveError={mutation.error}
              form={form}
              substations={substations}
              options={optionsQuery.data}
              source={editing}
              initialCompanyId={editing.distribution_company_id}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete feeder?</DialogTitle>
            <DialogDescription>
              This marks <span className="font-semibold text-foreground">{deleting?.feeder_name}</span>{" "}
              inactive.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleting && deleteMutation.mutate(deleting.feeder_id)}
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function FeederEditDialog({ feeder }: { feeder: Feeder }): JSX.Element {
  const [open, setOpen] = useState(false);
  const draft = useDialogDraft();
  const resetDraft = draft.reset;
  useEffect(() => {
    if (!open) resetDraft();
  }, [open, resetDraft]);
  const queryClient = useQueryClient();
  const substations = useQuery({ queryKey: ["substations"], queryFn: () => api.substations() });
  const options = useQuery({ queryKey: ["grid-options"], queryFn: api.gridOptions });
  const form = useForm<FeederValues>({
    resolver: zodResolver(feederSchema),
    shouldUnregister: false,
    defaultValues: feederValues(feeder),
  });
  const mutation = useMutation({
    mutationFn: (values: FeederValues) => api.updateFeeder(feeder.feeder_id, feederPayload(values)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["feeders"] });
      await queryClient.invalidateQueries({ queryKey: ["feeder", feeder.feeder_id] });
      setOpen(false);
    },
  });
  return <>
    <Button variant="outline" onClick={() => { form.reset(feederValues(feeder)); mutation.reset(); setOpen(true); }}>Edit feeder</Button>
    <Dialog open={open} onOpenChange={(next) => {
      if (next) setOpen(true);
      else if (draft.canClose(mutation.isPending)) setOpen(false);
    }}>
      <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-3xl"
        onChangeCapture={draft.onChangeCapture} onClickCapture={draft.onClickCapture} onKeyDownCapture={draft.onKeyDownCapture}>
        <FeederForm title={`Edit ${feeder.feeder_name}`} onSubmit={(values) => mutation.mutateAsync(values)}
          onCancel={() => { if (draft.canClose(mutation.isPending)) setOpen(false); }} submitting={mutation.isPending} saveError={mutation.error}
          form={form} substations={substations.data ?? []} options={options.data}
          source={feeder} initialCompanyId={feeder.distribution_company_id} />
      </DialogContent>
    </Dialog>
  </>;
}

function FeederForm({
  title,
  onSubmit,
  onCancel,
  submitting,
  form,
  substations,
  initialCompanyId,
  options,
  source,
  saveError,
}: {
  title: string;
  onSubmit: (values: FeederValues) => Promise<unknown>;
  onCancel: () => void;
  submitting: boolean;
  form: ReturnType<typeof useForm<FeederValues>>;
  substations: Substation[];
  initialCompanyId?: number;
  options?: GridOptions;
  source?: Feeder;
  saveError?: unknown;
}): JSX.Element {
  const [company, setCompany] = useState(initialCompanyId ? String(initialCompanyId) : "");
  const [step, setStep] = useState(0);
  const submittingRef = useRef(false);
  const stepFields: (keyof FeederValues)[][] = useMemo(() => [
    ["external_key", "display_name", "substation_id"],
    ["served_states", "regulators", "nerc_service_classes", "feeder_type", "feeder_type_detail", "voltage_kv", "feeder_band", "metered_customers", "unmetered_customers"],
    ["upstream_feeder_name", "upstream_voltage_kv", "source_voltage_kv", "latitude", "longitude", "power_transformer_id", "power_transformer_capacity_mva", "ct_vt_condition", "preferred_network", "iot_vendor", "iot_phase", "installation_type_available", "iot_installed", "remarks"],
    ["ct_primary", "ct_secondary", "vt_primary", "vt_secondary", "station_dc_nominal_v", "station_dc_min_v", "station_dc_max_v"],
  ], []);
  useEffect(() => {
    if (saveError) setStep(errorStep(saveError, stepFields) ?? 3);
  }, [saveError, stepFields]);
  async function nextStep(): Promise<void> {
    if (await form.trigger(stepFields[step], { shouldFocus: true })) setStep(step + 1);
  }
  const availableStations = substations.filter((station) =>
    String(station.distribution_company_id) === company,
  );
  return (
    <form className="flex min-h-0 flex-col gap-4" onSubmit={(event) => {
      if (step !== 3 || submitting || submittingRef.current) { event.preventDefault(); return; }
      void form.handleSubmit(async (values) => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try { await onSubmit(values); } catch { /* Mutation owns the error display. */ }
        finally { submittingRef.current = false; }
      }, (errors) => {
        const firstErrorStep = stepFields.findIndex((fields) => fields.some((field) => errors[field]));
        if (firstErrorStep >= 0) setStep(firstErrorStep);
      })(event);
    }} noValidate>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>Flat feeder CRUD used by controller meter assignments.</DialogDescription>
      </DialogHeader>
      <div className="min-h-0 overflow-y-auto">
      <div className="grid gap-3 sm:grid-cols-2">
        {step === 0 ? <>
        <h3 className="font-semibold sm:col-span-2">Identity and location</h3>
        <Field form={form} name="external_key" label="External key" readOnly={Boolean(source)} />
        <Field form={form} name="display_name" label="Feeder name" />
        <p className="text-xs text-muted-foreground sm:col-span-2">Keep the full official NERC feeder name, including the parts that distinguish it from other feeders. Station type and voltage also have separate fields.</p>
        <ListFilter label="Distribution company" value={company} emptyLabel="Select company"
          options={Array.from(new Map(substations.map((station) => [String(station.distribution_company_id), station.distribution_company_name])).entries()).map(([value, label]) => ({ value, label }))}
          onChange={(value) => { setCompany(value); form.setValue("substation_id", 0, { shouldValidate: true }); }} />
        <div className="space-y-2">
          <Label htmlFor="feeder-station">Substation</Label>
          <SearchableSelector label="Substation" size="form" id="feeder-station"
            placeholder="Select substation"
            options={availableStations.map((station) => substationPickerOption(station, availableStations))}
            disabled={!company}
            value={String(form.watch("substation_id") || "")}
            onChange={(value) => form.setValue("substation_id", Number(value), { shouldValidate: true })}
          />
          {!company ? <p className="text-xs text-muted-foreground">Choose a distribution company first.</p> : availableStations.length === 0 ? <p className="text-xs text-muted-foreground">No matching substations.</p> : null}
          {form.formState.errors.substation_id ? (
            <p className="text-xs text-destructive">Choose a substation.</p>
          ) : null}
        </div>
        </> : null}
        {step === 1 ? <>
        {(["served_states", "regulators", "nerc_service_classes"] as const).map((field) =>
          <CatalogChoice key={field} label={{ served_states: "Feeder states served (not station location)", regulators: "Regulators", nerc_service_classes: "Type (NERC)" }[field]}
            multiple options={options?.[field === "served_states" ? "states" : field] ?? []}
            value={form.watch(field)} onChange={(values) => form.setValue(field, values)} />)}
        <h3 className="font-semibold sm:col-span-2">Feeder and transformer</h3>
        <CatalogChoice label="Feeder topology type" options={options?.feeder_types ?? []} value={form.watch("feeder_type") ? [form.watch("feeder_type")!] : []}
          onChange={(values) => form.setValue("feeder_type", values[0] ?? null)} />
        <Field form={form} name="feeder_type_detail" label="Feeder type detail" />
        <CatalogChoice label="Own feeder voltage (kV)" options={options?.voltage_kv ?? []}
          value={form.watch("voltage_kv") === null ? [] : [String(form.watch("voltage_kv"))]}
          onChange={(values) => form.setValue("voltage_kv", values[0] ? Number(values[0]) : null)} />
        <CatalogChoice label="Feeder band" options={options?.feeder_bands ?? []} value={form.watch("feeder_band") ? [form.watch("feeder_band")] : []}
          onChange={(values) => form.setValue("feeder_band", values[0] ?? "")} />
        <Field form={form} name="metered_customers" label="Metered customers" type="number" />
        <Field form={form} name="unmetered_customers" label="Unmetered customers" type="number" />
        </> : null}
        {step === 2 ? <>
        <h3 className="font-semibold sm:col-span-2">Supply hierarchy</h3>
        <p className="text-xs text-muted-foreground sm:col-span-2">Own feeder voltage is recorded in Classification. Upstream identifies the incoming feeder; source voltage describes its transmission supply. Unknown values stay blank.</p>
        <Field form={form} name="upstream_feeder_name" label="Upstream feeder name" />
        {(["upstream_voltage_kv", "source_voltage_kv"] as const).map((field) =>
          <CatalogChoice key={field} label={field === "upstream_voltage_kv" ? "Upstream feeder voltage (kV)" : "Source transmission voltage (kV)"}
            options={options?.voltage_kv ?? []} value={form.watch(field) === null ? [] : [String(form.watch(field))]}
            onChange={(values) => form.setValue(field, values[0] ? Number(values[0]) : null)} />)}
        <h3 className="font-semibold sm:col-span-2">Installation</h3>
        <Field form={form} name="latitude" label="Latitude" type="number" />
        <Field form={form} name="longitude" label="Longitude" type="number" />
        <Field form={form} name="power_transformer_id" label="Transformer ID" />
        <Field form={form} name="power_transformer_capacity_mva" label="Transformer MVA" type="number" />
        <h3 className="font-semibold sm:col-span-2">Installation and communications</h3>
        <Field form={form} name="ct_vt_condition" label="CT/VT condition" />
        <Field form={form} name="preferred_network" label="Preferred network" />
        {(["iot_vendor", "iot_phase", "installation_type_available"] as const).map((field) =>
          <CatalogChoice key={field} label={{ iot_vendor: "IoT Vendor", iot_phase: "IoT installation phase", installation_type_available: "Recorded installation type" }[field]}
            options={options?.[{ iot_vendor: "iot_vendors", iot_phase: "iot_phases", installation_type_available: "installation_types" }[field] as keyof GridOptions] ?? []}
            value={form.watch(field) ? [form.watch(field)!] : []} onChange={(values) => form.setValue(field, values[0] ?? "")} />)}
        <CatalogChoice label="IoT installed" searchable={false} options={["Yes", "No"]} value={form.watch("iot_installed") === null ? [] : [form.watch("iot_installed") ? "Yes" : "No"]}
          onChange={(values) => form.setValue("iot_installed", !values[0] ? null : values[0] === "Yes")} />
        <p className="text-xs text-muted-foreground sm:col-span-2">IoT Vendor is the recorded installer association, not substation ownership or a user-access tenant.</p>
        {source ? <div className="space-y-1 text-xs text-muted-foreground sm:col-span-2">
          <p>Original source voltage: {source.source_voltage ?? "Unknown"}</p>
          <p>Source service area (not station address): {source.feeder_location ?? "Unknown"} · Major streets: {source.major_streets ?? "Unknown"}</p>
          {source.source_metadata && Object.keys(source.source_metadata).length > 0 ? <details><summary>Source provenance (read-only)</summary><pre className="overflow-x-auto whitespace-pre-wrap">{JSON.stringify(source.source_metadata, null, 2)}</pre></details> : null}
        </div> : null}
        <Field form={form} name="remarks" label="Remarks" />
        </> : null}
        {step === 3 ? <>
        <h3 className="font-semibold sm:col-span-2">CT/VT ratios</h3>
        <p className="text-sm text-muted-foreground sm:col-span-2">
          Ratios, including 1:1 defaults, are not engineering-verified by saving this form.
          Save ratios first, then use Verify CT/VT in the table. Ratio edits clear the corresponding confirmation.
        </p>
        <Field form={form} name="ct_primary" label="CT primary" type="number" />
        <Field form={form} name="ct_secondary" label="CT secondary" type="number" />
        <Field form={form} name="vt_primary" label="VT primary" type="number" />
        <Field form={form} name="vt_secondary" label="VT secondary" type="number" />
        <h3 className="font-semibold sm:col-span-2">Station DC supply</h3>
        <Field form={form} name="station_dc_nominal_v" label="DC nominal" type="number" />
        <Field form={form} name="station_dc_min_v" label="DC min" type="number" />
        <Field form={form} name="station_dc_max_v" label="DC max" type="number" />
        <section aria-label="Review feeder" className="space-y-1 rounded-md border p-3 text-sm sm:col-span-2">
          <h3 className="font-semibold">Review</h3>
          <p>Edited name: {form.watch("display_name")}</p>
          <p>Current saved name: {source?.feeder_name ?? "New feeder"}</p>
          <p>External key: {form.watch("external_key")}</p>
          <p>Station: {substations.find((station) => station.substation_id === form.watch("substation_id"))?.name ?? "Unknown"}</p>
          <p>Own feeder voltage: {form.watch("voltage_kv") === null ? "Unknown" : `${form.watch("voltage_kv")} kV`} · States: {form.watch("served_states").join(", ") || "Unknown"}</p>
          <p>Upstream feeder: {form.watch("upstream_feeder_name") || "Unknown"} · Upstream voltage: {form.watch("upstream_voltage_kv") === null ? "Unknown" : `${form.watch("upstream_voltage_kv")} kV`}</p>
          <p>Source transmission voltage: {form.watch("source_voltage_kv") === null ? "Unknown" : `${form.watch("source_voltage_kv")} kV`}</p>
          {source ? <div>
            <h4 className="font-semibold">Changed values</h4>
            {stepFields.flat().filter((field) =>
              JSON.stringify(form.formState.defaultValues?.[field]) !== JSON.stringify(form.getValues(field)),
            ).map((field) => <p key={field}>
              {field.replaceAll("_", " ")}: {JSON.stringify(form.formState.defaultValues?.[field] ?? "Unknown")} → {JSON.stringify(form.getValues(field) ?? "Unknown")}
            </p>)}
          </div> : null}
        </section>
        </> : null}
      </div>
      {saveError ? <p role="alert" className="text-sm text-destructive">{saveError instanceof Error ? saveError.message : "Could not save feeder."}</p> : null}
      </div>
      <FormStepNavigation steps={["Identity & Station", "Classification & NERC", "Installation", "Electrical Ratios & Review"]}
        step={step} onBack={() => setStep(step - 1)} onNext={() => { void nextStep(); }} busy={submitting} />
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} type="button">
          Cancel
        </Button>
        {step === 3 ? <Button type="submit" disabled={submitting}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save feeder
        </Button> : null}
      </DialogFooter>
    </form>
  );
}

function Field({
  form,
  name,
  label,
  type = "text",
  readOnly = false,
}: {
  form: ReturnType<typeof useForm<FeederValues>>;
  name: keyof FeederValues;
  label: string;
  type?: "text" | "number";
  readOnly?: boolean;
}): JSX.Element {
  const nullableNumber = ["latitude", "longitude", "power_transformer_capacity_mva",
    "metered_customers", "unmetered_customers",
    "station_dc_nominal_v", "station_dc_min_v", "station_dc_max_v"].includes(name);
  return (
    <div className="space-y-2">
      <Label htmlFor={String(name)}>{label}</Label>
      <Input
        id={String(name)}
        type={type}
        readOnly={readOnly}
        step={type === "number" ? "any" : undefined}
        {...form.register(name, type === "number"
          ? nullableNumber
            ? { setValueAs: (value: string | number | null) => value === "" || value == null ? null : Number(value) }
            : { valueAsNumber: true }
          : undefined)}
      />
      {form.formState.errors[name] ? (
        <p role="alert" className="text-xs text-destructive">{form.formState.errors[name]?.message}</p>
      ) : null}
    </div>
  );
}
